import { ToastLevel } from '@src/common/components/ToastLevel';
import { useToast } from '@src/common/hooks/useToast';
import { useNavigateToOrderDetails } from '@src/orders/hooks/useNavigateToOrderDetails';
import { useRefreshOrder } from '@src/orders/hooks/useRefreshOrder';
import { useRefreshOrders } from '@src/orders/hooks/useRefreshOrders';
import type { FeesFormFields } from '@src/orders/schemas/ReviewOrderFormPermitFields';
import { OrderInvoicesClient } from '@src/orders/services/OrderInvoiceClient';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

import { QuickbookError } from '../review/QuickbookError';

export const useInvoice = ({ orderId }: { orderId: string }) => {
  const { t } = useTranslation();
  const { navigateToOrderDetails } = useNavigateToOrderDetails();
  const { showToast } = useToast();
  const { refreshOrders } = useRefreshOrders();
  const { refreshOrder } = useRefreshOrder({ id: orderId });

  const { mutate: mutateInvoice, isPending: isGeneratingInvoice } = useMutation(
    {
      mutationFn: (cta: () => Promise<void>) => cta(),
      onSuccess: () => {
        refreshOrders();
        refreshOrder();
        void navigateToOrderDetails({ orderId });
        showToast({
          severity: ToastLevel.success,
          title: t('editOrder.generateInvoice.successTitle'),
          message: t('editOrder.generateInvoice.successMessage'),
        });
      },
      onError: (
        error: AxiosError<{ error: string; message: string; type: string }>,
      ) => {
        refreshOrder();
        showToast({
          severity: ToastLevel.error,
          message: <QuickbookError error={error.response?.data} />,
        });
      },
    },
  );

  const { mutate: mutateSendEmail, isPending: isEmailPending } = useMutation({
    mutationFn: (cta: () => Promise<{ customerEmails: string[] }>) => cta(),
    onSuccess: (data) => {
      refreshOrders();
      refreshOrder();
      void navigateToOrderDetails({ orderId });
      const uniqueEmails = Array.from(new Set(data.customerEmails));
      const emails =
        uniqueEmails.length === 1 ? uniqueEmails[0] : uniqueEmails.join(', ');

      showToast({
        severity: ToastLevel.success,
        title: t('editOrder.sendInvoice.successTitle'),
        message: t('editOrder.sendInvoice.successMessage', {
          count: data.customerEmails.length,
          email: emails,
        }),
      });
    },
    onError: (
      error: AxiosError<{ error: string; message: string; type: string }>,
    ) =>
      showToast({
        severity: ToastLevel.error,
        message: <QuickbookError error={error.response?.data} />,
      }),
  });

  const { mutate: mutateEditInvoice, isPending: isEditingInvoice } =
    useMutation({
      mutationFn: (cta: () => Promise<void>) => cta(),
      onSuccess: (_) => {
        refreshOrders();
        refreshOrder();
      },
      onError: (
        error: AxiosError<{ error: string; message: string; type: string }>,
      ) =>
        showToast({
          severity: ToastLevel.error,
          message: <QuickbookError error={error.response?.data} />,
        }),
    });

  const { mutate: mutateDeleteInvoice, isPending: isDeletingInvoice } =
    useMutation({
      mutationFn: (cta: () => Promise<void>) => cta(),
      onSuccess: () => {
        refreshOrders();
        refreshOrder();
        showToast({
          severity: ToastLevel.success,
          message: t('editOrder.deleteInvoice.success'),
        });
      },
      onError: (
        error: AxiosError<{ error: string; message: string; type: string }>,
      ) =>
        showToast({
          severity: ToastLevel.error,
          message: <QuickbookError error={error.response?.data} />,
        }),
    });

  const generateInvoice = (billingDate?: string) => {
    mutateInvoice(() =>
      OrderInvoicesClient.generateInvoice({
        orderId,
        billingDate,
      }),
    );
  };

  const modifyInvoice = () => {
    mutateInvoice(() =>
      OrderInvoicesClient.modifyInvoice({
        orderId,
      }),
    );
  };

  const editInvoice = () => {
    mutateEditInvoice(() =>
      OrderInvoicesClient.editInvoice({
        orderId,
      }),
    );
  };

  const deleteInvoice = () => {
    mutateDeleteInvoice(() =>
      OrderInvoicesClient.deleteInvoice({
        orderId,
      }),
    );
  };

  const sendInvoiceByEmail = ({
    orderId,
    formValues,
  }: {
    orderId: string;
    formValues: FeesFormFields;
  }) => {
    mutateSendEmail(() =>
      OrderInvoicesClient.sendInvoiceEmail({
        orderId,
        fields: formValues,
      }),
    );
  };

  return {
    modifyInvoice,
    generateInvoice,
    editInvoice,
    deleteInvoice,
    sendInvoiceByEmail,
    invoiceActionStatus: {
      isGeneratingInvoice,
      isEmailPending,
      isDeletingInvoice,
      isEditingInvoice,
      isDisabled:
        isGeneratingInvoice ||
        isEmailPending ||
        isDeletingInvoice ||
        isEditingInvoice,
    },
  };
};
